import { useCallback, useEffect, useState } from "react";
import axiosInstance from "@/utils/appsettings";

// The plugin route. Worth knowing why it exists: Figma's REST API meters reads per user and a
// restricted seat allows roughly six a month, which is under one import. The plugin runs inside
// the user's own Figma, so it has no rate limit and needs no paid seat.

export interface PluginTokenInfo {
  tokenPrefix: string;
  label: string;
  createdAt: string | null;
  lastUsedAt: string | null;
  isActive: boolean;
}

export type FigmaTarget = "design" | "fullstack" | "mobile";

export interface FigmaTargetOption {
  key: FigmaTarget;
  label: string;
  summary: string;
  recommended: boolean;
  reason: string;
}

/** What the design's own dimensions say it should be built as. */
export interface FigmaRecommendation {
  target: FigmaTarget;
  reason: string;
  confidence: "high" | "medium" | "low";
  phoneScreens: number;
  tabletScreens: number;
  desktopScreens: number;
  minWidth: number;
  maxWidth: number;
  options: FigmaTargetOption[];
}

export interface FigmaHandoff {
  handoffId: string;
  fileName: string;
  screens: number;
  /** Undefined on the link route, where assets are exported during the build rather than up front. */
  assetCount?: number;
  renderCount?: number;
  cssAvailable: boolean;
  notices: string[];
  recommendation?: FigmaRecommendation;
}

const errorMessage = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: { error?: { message?: string } } } } })
    ?.response?.data?.detail?.error?.message;
  return detail || fallback;
};

export const useFigmaPluginToken = () => {
  const [info, setInfo] = useState<PluginTokenInfo | null>(null);
  const [issued, setIssued] = useState(false);
  // Held in memory only — the server shows the plaintext once and never again
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/figma/plugin/token");
      setIssued(Boolean(res.data?.data?.issued));
      setInfo(res.data?.data?.token ?? null);
    } catch (err) {
      setError(errorMessage(err, "Could not check your plugin token."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const issue = useCallback(async () => {
    setError(null);
    try {
      const res = await axiosInstance.post("/figma/plugin/token");
      const token = res.data?.data?.token ?? null;
      setPlaintext(token);
      await refresh();
      return token;
    } catch (err) {
      setError(errorMessage(err, "Could not create a plugin token."));
      return null;
    }
  }, [refresh]);

  const revoke = useCallback(async () => {
    setError(null);
    try {
      await axiosInstance.delete("/figma/plugin/token");
      setPlaintext(null);
      await refresh();
    } catch (err) {
      setError(errorMessage(err, "Could not revoke the plugin token."));
    }
  }, [refresh]);

  /** Clears the one-time plaintext from memory once the user has copied it. */
  const dismissPlaintext = useCallback(() => setPlaintext(null), []);

  return { info, issued, plaintext, loading, error, refresh, issue, revoke, dismissPlaintext };
};

/**
 * The newest design waiting from the plugin, independent of the URL.
 *
 * The plugin returns a link carrying the handoff id, but a user who uploads from Figma and then
 * switches to their existing Joylo tab never visits it — so a successful upload looked like nothing
 * had happened. This asks the server directly instead.
 */
const SEEN_HANDOFFS_KEY = "joylo:figma-handoffs-seen";

const readSeen = (): string[] => {
  try {
    const raw = window.localStorage.getItem(SEEN_HANDOFFS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(-20) : [];
  } catch {
    return [];
  }
};

/** Records a handoff as already offered, so the banner is a moment rather than a fixture. */
export const markHandoffSeen = (handoffId: string) => {
  if (!handoffId) return;
  try {
    const seen = readSeen();
    if (!seen.includes(handoffId)) {
      window.localStorage.setItem(SEEN_HANDOFFS_KEY, JSON.stringify([...seen, handoffId].slice(-20)));
    }
  } catch {
    // Private browsing can refuse storage; the banner reappearing is a far smaller problem
  }
};

export const usePendingFigmaHandoff = (enabled = true) => {
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await axiosInstance.get("/figma/plugin/pending");
        const id = res.data?.data?.handoffId ?? null;
        // Offered once. A parked upload lives for 24 hours, so without this the banner reappeared on
        // every visit to the home page long after the user had moved on — it read as a stuck
        // notification rather than the arrival of something new.
        if (!cancelled) setPendingId(id && !readSeen().includes(id) ? id : null);
      } catch {
        // Not signed in, no Redis, or nothing waiting — all mean "no banner", never an error
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Marked on unmount, not on render: navigating away is the signal that the user has seen it and
  // chosen to do something else
  useEffect(
    () => () => {
      if (pendingId) markHandoffSeen(pendingId);
    },
    [pendingId],
  );

  return pendingId;
};

export const useFigmaHandoff = (handoffId?: string | null) => {
  const [handoff, setHandoff] = useState<FigmaHandoff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handoffId) {
      setHandoff(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await axiosInstance.get(`/figma/plugin/pending/${handoffId}`);
        if (!cancelled) setHandoff(res.data?.data ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, "That design upload has expired. Re-run the plugin in Figma."));
          setHandoff(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handoffId]);

  const build = useCallback(
    async (projectId: string, prompt = "") => {
      if (!handoffId) return false;
      try {
        await axiosInstance.post("/figma/plugin/build", {
          handoff_id: handoffId,
          project_id: projectId,
          prompt,
        });
        return true;
      } catch (err) {
        setError(errorMessage(err, "Could not start the build. Please try again."));
        return false;
      }
    },
    [handoffId],
  );

  return { handoff, loading, error, build };
};
